import { lazy, Suspense, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useIdentity, useSession } from "@/features/auth/hooks/use-session";
import { ensureClientRows, useClientRows } from "@/features/clients/lib/row-store";
import { orderTabPath } from "@/features/order-instructions/lib/order-stages";
import type { ClientListItem } from "@/domain/clients/list-types";
import { signOut } from "@/shared/api/session";
import { formatAmount } from "@/shared/ui/primitives/AmountField";
import { type TranslateFn, useTranslation } from "@/shared/i18n/LocaleProvider";
import type { AdvisorProfile } from "@/shared/api/identity";
import { IconButton } from "@/shared/ui/primitives/IconButton";
import { initials } from "@/shared/ui/primitives/Avatar";
import { SegmentedControl } from "@/shared/ui/primitives/SegmentedControl";
import { SideItem, SideMenu, SideSearch, SideSection } from "@/shared/ui/layout/SideMenu";
import { ChatIcon, LogoutIcon, SettingsIcon, UserIcon } from "@/shared/ui/layout/shell-icons";
import { clientPath, DOCK_PATHS } from "@/shared/ui/dock-pages";
import {
  groupWaiting,
  useWaitingItems,
  type WaitingGroup,
  type WaitingItems,
  type WaitingKind,
} from "@/app/workspace/waiting-items";
import type { Locale } from "@/shared/i18n";

export function ShellSideMenu({
  collapsed,
  onToggle,
  onAllChats,
}: {
  collapsed: boolean;
  onToggle: () => void;
  onAllChats: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [query, setQuery] = useState("");
  const { rows } = useClientRows();
  const waiting = useWaitingItems(!collapsed);

  useEffect(() => {
    if (!collapsed) void ensureClientRows();
  }, [collapsed]);

  const matches = (rows ?? []).filter((row) =>
    row.name.toLowerCase().includes(query.trim().toLowerCase()),
  );

  return (
    <SideMenu
      collapsed={collapsed}
      onToggle={onToggle}
      labels={{
        open: t("shell.side.open"),
        collapse: t("shell.side.collapse"),
        navigation: t("shell.side.navigation"),
      }}
      search={
        <SideSearch
          value={query}
          onChange={setQuery}
          onEnter={() => {
            if (query.trim() && matches.length > 0) navigate(clientPath(matches[0].id));
          }}
          label={t("shell.side.search")}
        />
      }
      footer={<SideFooter />}
      miniFooter={
        <>
          <IconButton label={t("shell.side.allChats")} onClick={onAllChats}>
            <ChatIcon />
          </IconButton>
          <IconButton
            label={t("shell.side.settings")}
            onClick={() => navigate(DOCK_PATHS.settings)}
          >
            <SettingsIcon />
          </IconButton>
          <IconButton label={t("shell.side.logout")} onClick={signOutToScreen}>
            <LogoutIcon />
          </IconButton>
        </>
      }
    >
      <Suspense fallback={null}>
        <ChatHistoryList recentLimit={SIDE_RECENT_CHATS} />
      </Suspense>
      <SideSection
        title={t("shell.side.waiting")}
        count={
          waiting?.done && !waiting.failed && waiting.unread === 0 ? waiting.items.length : null
        }
      >
        <WaitingRows waiting={waiting} />
      </SideSection>
      <SideSection title={t("shell.side.clients")} count={rows ? matches.length : null}>
        {rows === null ? (
          <SideNote>{t("shell.side.loading")}</SideNote>
        ) : matches.length === 0 ? (
          <SideNote>
            {rows.length === 0 ? t("shell.side.noClients") : t("shell.side.noMatches")}
          </SideNote>
        ) : (
          matches.map((row) => (
            <SideItem
              key={`${row.kind}:${row.id}`}
              badge={initials(row.name, 2)}
              badgeKey={row.partyId}
              title={row.name}
              sub={clientSubline(row, t)}
              active={pathname === clientPath(row.id)}
              onClick={() => navigate(clientPath(row.id))}
            />
          ))
        )}
      </SideSection>
    </SideMenu>
  );
}

function WaitingRows({ waiting }: { waiting: WaitingItems | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (waiting === null) return <SideNote>{t("shell.side.loading")}</SideNote>;
  if (waiting.failed) return <SideNote>{t("shell.side.waitingFailed")}</SideNote>;
  const groups = groupWaiting(waiting.items);
  const shown = groups.slice(0, MAX_WAITING_ROWS);
  if (groups.length === 0) {
    return (
      <SideNote>
        {!waiting.done
          ? t("shell.side.loading")
          : waiting.unread > 0
            ? t("shell.side.nothingWaitingRead")
            : t("shell.side.nothingWaiting")}
      </SideNote>
    );
  }
  return (
    <>
      {shown.map((group) => (
        <SideItem
          key={group.kind}
          badge={WAITING_BADGE[group.kind]}
          title={t(WAITING_TITLE[group.kind])}
          count={group.items.length > 1 ? group.items.length : null}
          sub={waitingSubline(group)}
          dot
          onClick={() => navigate(waitingTarget(group))}
        />
      ))}
      {!waiting.done && <SideNote>{t("shell.side.loading")}</SideNote>}
      {waiting.items.length > shown.length && (
        <li>
          <SideLinkButton
            label={t("shell.side.showAll")}
            onClick={() => navigate(showAllTarget(groups))}
          />
        </li>
      )}
    </>
  );
}

function SideLinkButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="cursor-pointer rounded-[8px] px-[10px] py-[6px] text-sm text-(--brand) hover:bg-(--paper) focus-visible:outline-2 focus-visible:outline-(--brand)"
    >
      {label}
    </button>
  );
}

function SideFooter() {
  const { t, locale, setLocale } = useTranslation();
  const navigate = useNavigate();
  const { identity } = useIdentity();
  const { data } = useSession();
  const user =
    identity?.displayName?.trim() || identity?.userName?.trim() || data?.user.name.trim() || "";
  const representation = representationLine(identity?.advisorProfile ?? null, user, t);
  return (
    <>
      <div className="flex min-w-0 flex-col gap-[2px]">
        <div className="flex min-w-0 items-center gap-[7px] text-(--muted)">
          <UserIcon size={14} />
          <span className="min-w-0 truncate text-sm" title={user}>
            {t("shell.side.signedInAs")} <b className="font-medium text-(--ink)">{user}</b>
          </span>
        </div>
        {representation && (
          <span className="truncate pl-[21px] text-xs text-(--muted)" title={representation}>
            {representation}
          </span>
        )}
      </div>
      <div className="flex items-center gap-[6px]">
        <SegmentedControl<Locale>
          label={t("shell.side.language")}
          size="sm"
          value={locale}
          onChange={setLocale}
          options={[
            { value: "sv", label: "SV" },
            { value: "en", label: "EN" },
          ]}
        />
        <span className="flex-1" />
        <IconButton label={t("shell.side.settings")} onClick={() => navigate(DOCK_PATHS.settings)}>
          <SettingsIcon size={16} />
        </IconButton>
        <button
          type="button"
          onClick={signOutToScreen}
          className="inline-flex cursor-pointer items-center gap-[6px] rounded-[8px] px-[8px] py-[6px] text-sm text-(--dim) hover:bg-(--fill) hover:text-(--ink) focus-visible:outline-2 focus-visible:outline-(--brand)"
        >
          <LogoutIcon size={16} />
          {t("shell.side.logout")}
        </button>
      </div>
    </>
  );
}

function representationLine(
  profile: AdvisorProfile | null,
  user: string,
  t: TranslateFn,
): string | null {
  if (!profile) return null;
  const name = `${profile.firstName} ${profile.lastName}`.trim();
  if (name.toLowerCase() === user.toLowerCase()) return profile.corporationName;
  return t("shell.side.advisorFor", { name, firm: profile.corporationName });
}

function SideNote({ children }: { children: string }) {
  return <li className="px-[10px] py-[6px] text-sm text-(--muted)">{children}</li>;
}

function clientSubline(row: ClientListItem, t: (key: string) => string): string {
  const kind =
    row.kind === "corporation" ? t("shell.side.corporation") : t("shell.side.individual");
  return typeof row.totalValue === "number" ? `${kind} · ${formatAmount(row.totalValue)} kr` : kind;
}

function waitingSubline(group: WaitingGroup): string {
  const [first] = group.items;
  const lead = first.portfolioName
    ? `${first.clientName} · ${first.portfolioName}`
    : first.clientName;
  const others = group.clientIds.length - 1;
  return others > 0 ? `${lead} +${others}` : lead;
}

function waitingTarget(group: WaitingGroup): string {
  return orderTabPath(group.kind === "to-confirm" ? "exec" : "signed");
}

function showAllTarget(groups: readonly WaitingGroup[]): string {
  return orderTabPath(groups.every((group) => group.kind === "to-confirm") ? "exec" : "signed");
}

function signOutToScreen(): void {
  void signOut({ callbackUrl: SIGNED_OUT_PATH });
}

const SIGNED_OUT_PATH = "/auth/signed-out";

const MAX_WAITING_ROWS = 5;

const SIDE_RECENT_CHATS = 8;

const ChatHistoryList = lazy(async () => ({
  default: (await import("@/features/intelligence/components/ChatHistoryList")).ChatHistoryList,
}));

const WAITING_BADGE: Record<WaitingKind, string> = {
  "signed-order": "SO",
  "to-confirm": "OI",
};

const WAITING_TITLE: Record<WaitingKind, string> = {
  "signed-order": "shell.side.signedOrder",
  "to-confirm": "shell.side.toConfirm",
};
